import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Badge,
  Table,
  Spinner,
  Alert,
  Button
} from "reactstrap";
import {
  Calendar,
  Clock,
  User,
  MapPin,
  Wrench,
  Users,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Wallet,
  BarChart3,
  PieChart,
  Activity,
  Target,
  Briefcase,
  CreditCard,
  FileText,
  Star
} from "lucide-react";
import { ClipLoader } from "react-spinners";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { useNavigate } from "react-router-dom";

// Import new consolidated API and branch check API
import { getSpaManagerFinanceDashboard } from "../../components/Api/GmsAdminDashboard";
import { getBranches } from "../../components/Api/GmsBranch";

const SpaManagerFinanceDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 30)),
    end: new Date()
  });

  const [branchConfig, setBranchConfig] = useState({
    allowSales: true,
    allowServices: true
  });

  const [dashboardData, setDashboardData] = useState({
    // Managerial Overview
    teamPerformance: {
      totalEmployees: 0,
      activeEmployees: 0,
      onLeave: 0,
      productivity: 0
    },
    dailyOperations: {
      todaysBookings: 0,
      completedBookings: 0,
      cancelledBookings: 0,
      rescheduledBookings: 0
    },
    resourceUtilization: {
      roomOccupancy: 0,
      equipmentUsage: 0,
      totalRooms: 0,
      totalEquipment: 0
    },
    staffScheduling: {
      scheduledEmployees: 0,
      leaveRequests: 0,
      coverageNeeds: 0
    },

    // Financial Analytics
    revenueData: {
      dailyRevenue: 0,
      monthlyRevenue: 0,
      serviceRevenue: 0,
      productRevenue: 0,
      revenueGrowth: 0
    },
    payrollOverview: {
      totalPayroll: 0,
      pendingAdvances: 0,
      approvedAdvances: 0,
      payrollCost: 0
    },
    profitability: {
      grossRevenue: 0,
      operationalCosts: 0,
      netProfit: 0,
      profitMargin: 0
    },

    // Business Intelligence
    customerAnalytics: {
      totalCustomers: 0,
      newCustomers: 0,
      returningCustomers: 0,
      customerGrowth: 0
    },
    servicePerformance: {
      totalServices: 0,
      popularServices: [],
      serviceRevenue: 0,
      averageServiceValue: 0
    },
    operationalEfficiency: {
      averageBookingTime: 0,
      serviceCompletionRate: 0,
      resourceUtilizationRate: 0
    }
  });

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaManagerFinanceDashboard');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSpaManagerFinanceDashboard');
  const isAdmin = isUserAdmin();

  useEffect(() => {
    if (hasPermission) {
      loadDashboardData();
    } else {
      // Check if user has HR Dashboard permission
      const hrDashboardPermission = hasPagePermissionWithAdmin(permissions, '/Dashboard');
      if (hrDashboardPermission) {
        navigate('/Dashboard');
        return;
      }
      // Check if user has Spa Employee Dashboard permission
      const spaDashboardPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaEmployeeDashboard');
      if (spaDashboardPermission) {
        navigate('/GmsSpaEmployeeDashboard');
        return;
      }
      navigate('/Dashboard');
    }
  }, [hasPermission, selectedDateRange]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const startDate = selectedDateRange.start.toISOString().split('T')[0];
      const endDate = selectedDateRange.end.toISOString().split('T')[0];
      const today = new Date().toISOString().split('T')[0];

      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const businessId = authUser.companyId || user.user_Company_Id || null;

      let branchId = null;
      const sessionBranchStr = sessionStorage.getItem('selectedBranch');
      if (sessionBranchStr) {
        try {
          const selectedBranch = JSON.parse(sessionBranchStr);
          if (selectedBranch && selectedBranch.value) {
            branchId = Number(selectedBranch.value);
          }
        } catch (e) {}
      }
      if (!branchId) {
        branchId = authUser.branchId || user.user_Branch_Id || null;
      }

      // Load branch configuration details
      let allowSales = true;
      let allowServices = true;
      if (branchId) {
        try {
          const response = await getBranches({ Branch_Id: branchId });
          const branchData = Array.isArray(response) ? response : (response.data || []);
          if (branchData && branchData.length > 0) {
            allowSales = branchData[0].branch_AllowSales === true;
            allowServices = branchData[0].branch_AllowServices === true;
          }
        } catch (error) {
          console.error("Error loading branch configuration:", error);
        }
      }
      setBranchConfig({ allowSales, allowServices });

      const filter = {
        businessId: businessId ? Number(businessId) : null,
        branchId: branchId ? Number(branchId) : null,
        startDate: startDate,
        endDate: endDate,
        today: today
      };

      const response = await getSpaManagerFinanceDashboard(filter);

      if (response && response.isSuccess) {
        const teamPerf = response.teamPerformance || {};
        const dailyOps = response.dailyOperations || {};
        const resUtil = response.resourceUtilization || {};
        const revData = response.revenueData || {};
        const payroll = response.payrollOverview || {};
        const custAnalytics = response.customerAnalytics || {};
        const popServices = response.popularServices || [];
        const opEfficiency = response.operationalEfficiency || {};

        setDashboardData({
          teamPerformance: {
            totalEmployees: teamPerf.totalEmployees || 0,
            activeEmployees: teamPerf.activeEmployees || 0,
            onLeave: teamPerf.onLeave || 0,
            productivity: teamPerf.activeEmployees > 0 ?
              ((teamPerf.activeEmployees - teamPerf.onLeave) / teamPerf.activeEmployees * 100) : 0
          },
          dailyOperations: {
            todaysBookings: dailyOps.todaysBookings || 0,
            completedBookings: dailyOps.completedBookings || 0,
            cancelledBookings: dailyOps.cancelledBookings || 0,
            rescheduledBookings: dailyOps.rescheduledBookings || 0
          },
          resourceUtilization: {
            totalRooms: resUtil.totalRooms || 0,
            totalEquipment: resUtil.totalEquipment || 0,
            occupiedRooms: resUtil.occupiedRooms || 0,
            usedEquipment: resUtil.usedEquipment || 0,
            roomOccupancy: resUtil.totalRooms > 0 ? (resUtil.occupiedRooms / resUtil.totalRooms * 100) : 0,
            equipmentUsage: resUtil.totalEquipment > 0 ? (resUtil.usedEquipment / resUtil.totalEquipment * 100) : 0
          },
          staffScheduling: {
            scheduledEmployees: teamPerf.activeEmployees || 0,
            leaveRequests: teamPerf.onLeave || 0,
            coverageNeeds: teamPerf.onLeave || 0
          },
          revenueData: {
            dailyRevenue: revData.dailyRevenue || 0,
            monthlyRevenue: revData.monthlyRevenue || 0,
            serviceRevenue: revData.serviceRevenue || 0,
            productRevenue: (revData.monthlyRevenue || 0) - (revData.serviceRevenue || 0),
            revenueGrowth: 0
          },
          payrollOverview: {
            totalPayroll: payroll.totalPayroll || 0,
            pendingAdvances: payroll.pendingAdvances || 0,
            approvedAdvances: payroll.approvedAdvances || 0,
            payrollCost: (payroll.totalPayroll || 0) + (payroll.totalAdvanceAmount || 0)
          },
          profitability: {
            grossRevenue: revData.monthlyRevenue || 0,
            operationalCosts: (payroll.totalPayroll || 0) + (payroll.totalAdvanceAmount || 0),
            netProfit: (revData.monthlyRevenue || 0) - ((payroll.totalPayroll || 0) + (payroll.totalAdvanceAmount || 0)),
            profitMargin: revData.monthlyRevenue > 0 ?
              (((revData.monthlyRevenue || 0) - ((payroll.totalPayroll || 0) + (payroll.totalAdvanceAmount || 0))) / revData.monthlyRevenue * 100) : 0
          },
          customerAnalytics: {
            totalCustomers: custAnalytics.totalCustomers || 0,
            newCustomers: custAnalytics.newCustomers || 0,
            returningCustomers: custAnalytics.returningCustomers || 0,
            customerGrowth: custAnalytics.totalCustomers > 0 ? (custAnalytics.newCustomers / custAnalytics.totalCustomers * 100) : 0
          },
          servicePerformance: {
            totalServices: 0,
            popularServices: popServices.map(s => ({
              serviceName: s.serviceName,
              bookings: s.bookings,
              revenue: s.revenue
            }))
          },
          operationalEfficiency: {
            averageBookingTime: opEfficiency.averageBookingTime || 0,
            serviceCompletionRate: opEfficiency.serviceCompletionRate || 0
          }
        });
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const currencyCode = localStorage.getItem("currencyCode") || "AED";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const getStatusBadge = (status, type = 'default') => {
    const statusConfig = {
      'success': { color: 'success', bgColor: '#d4edda', textColor: '#155724' },
      'warning': { color: 'warning', bgColor: '#fff3cd', textColor: '#856404' },
      'danger': { color: 'danger', bgColor: '#f8d7da', textColor: '#721c24' },
      'info': { color: 'info', bgColor: '#d1ecf1', textColor: '#0c5460' }
    };

    const config = statusConfig[type] || statusConfig['info'];

    return (
      <span
        className="badge"
        style={{
          backgroundColor: config.bgColor,
          color: config.textColor,
          border: '1px solid rgba(0,0,0,0.1)'
        }}
      >
        {status}
      </span>
    );
  };

  if (!hasPermission) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="danger">
            You don't have permission to access this page.
          </Alert>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <ClipLoader color="#556ee6" size={50} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Spa Manager & Finance Dashboard" breadcrumbItem="Dashboard" />


        {/* Managerial Overview */}
        <Row className="mb-4">
          <Col lg={12}>
            <h4 className="mb-3">
              <Briefcase className="me-2" size={24} />
              Managerial Overview
            </h4>
          </Col>
        </Row>

        <Row className="mb-4">
          {/* Team Performance */}
          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-2">{dashboardData.teamPerformance.totalEmployees}</h4>
                    <p className="text-muted mb-0">Total Employees</p>
                    <small className="text-success">
                      {dashboardData.teamPerformance.activeEmployees} Active
                    </small>
                  </div>
                  <div className="avatar-sm rounded-circle bg-primary bg-soft p-2">
                    <Users className="font-size-24" />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Today's Bookings OR Today's Revenue based on allowServices */}
          {branchConfig.allowServices ? (
            <Col lg={3} md={6} className="mb-3">
              <Card className="h-100">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h4 className="mb-2">{dashboardData.dailyOperations.todaysBookings}</h4>
                      <p className="text-muted mb-0">Today's Bookings</p>
                      <small className="text-info">
                        {dashboardData.dailyOperations.completedBookings} Completed
                      </small>
                    </div>
                    <div className="avatar-sm rounded-circle bg-info bg-soft p-2">
                      <Calendar className="font-size-24" />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ) : (
            <Col lg={3} md={6} className="mb-3">
              <Card className="h-100">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <h4 className="mb-2">{formatCurrency(dashboardData.revenueData.dailyRevenue)}</h4>
                      <p className="text-muted mb-0">Today's Revenue</p>
                      <small className="text-success">
                        Product Sales
                      </small>
                    </div>
                    <div className="avatar-sm rounded-circle bg-info bg-soft p-2">
                      <TrendingUp className="font-size-24" />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}

          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-2">{dashboardData.staffScheduling.leaveRequests}</h4>
                    <p className="text-muted mb-0">Leave Requests</p>
                    <small className="text-danger">
                      {dashboardData.staffScheduling.coverageNeeds} Coverage Needed
                    </small>
                  </div>
                  <div className="avatar-sm rounded-circle bg-danger bg-soft p-2">
                    <AlertCircle className="font-size-24" />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-2">{dashboardData.customerAnalytics.newCustomers}</h4>
                    <p className="text-muted mb-0">New Customers</p>
                    <small className="text-success">
                      {formatPercentage(dashboardData.customerAnalytics.customerGrowth)} Growth Rate
                    </small>
                  </div>
                  <div className="avatar-sm rounded-circle bg-success bg-soft p-2">
                    <Users className="font-size-24" />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>



        {/* Financial Analytics */}
        <Row className="mb-4">
          <Col lg={12}>
            <h4 className="mb-3">
              <Wallet className="me-2" size={24} />
              Financial Analytics
            </h4>
          </Col>
        </Row>

        <Row className="mb-4">
          {/* Revenue Overview */}
          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-2">{formatCurrency(dashboardData.revenueData.monthlyRevenue)}</h4>
                    <p className="text-muted mb-0">Monthly Revenue</p>
                    <small className="text-success">
                      {branchConfig.allowServices 
                        ? `Service: ${formatCurrency(dashboardData.revenueData.serviceRevenue)}` 
                        : `Product Sales: ${formatCurrency(dashboardData.revenueData.productRevenue)}`}
                    </small>
                  </div>
                  <div className="avatar-sm rounded-circle bg-success bg-soft p-2">
                    <TrendingUp className="font-size-24" />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-2">{formatCurrency(dashboardData.profitability.netProfit)}</h4>
                    <p className="text-muted mb-0">Net Profit</p>
                    <small className="text-info">
                      Margin: {formatPercentage(dashboardData.profitability.profitMargin)}
                    </small>
                  </div>
                  <div className="avatar-sm rounded-circle bg-info bg-soft p-2">
                    <BarChart3 className="font-size-24" />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-2">{formatCurrency(dashboardData.payrollOverview.payrollCost)}</h4>
                    <p className="text-muted mb-0">Payroll Cost</p>
                    <small className="text-warning">
                      {dashboardData.payrollOverview.pendingAdvances} Pending Advances
                    </small>
                  </div>
                  <div className="avatar-sm rounded-circle bg-warning bg-soft p-2">
                    <CreditCard className="font-size-24" />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-2">{formatCurrency(dashboardData.revenueData.productRevenue)}</h4>
                    <p className="text-muted mb-0">Product Revenue</p>
                    <small className="text-primary">
                      Total: {formatCurrency(dashboardData.revenueData.monthlyRevenue)}
                    </small>
                  </div>
                  <div className="avatar-sm rounded-circle bg-primary bg-soft p-2">
                    <FileText className="font-size-24" />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4">
          {branchConfig.allowServices ? (
            <>
              <Col lg={6}>
                <Card>
                  <CardHeader>
                    <CardTitle className="d-flex align-items-center gap-2">
                      <PieChart className="text-primary" size={20} />
                      Revenue Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col md={6}>
                        <div className="text-center">
                          <h3 className="text-success">{formatCurrency(dashboardData.revenueData.serviceRevenue)}</h3>
                          <p className="text-muted">Service Revenue</p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-center">
                          <h3 className="text-info">{formatCurrency(dashboardData.revenueData.productRevenue)}</h3>
                          <p className="text-muted">Product Revenue</p>
                        </div>
                      </Col>
                    </Row>
                    <div className="mt-3">
                      <div className="d-flex justify-content-between">
                        <span>Total Revenue</span>
                        <span className="text-primary fw-bold">{formatCurrency(dashboardData.revenueData.monthlyRevenue)}</span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col lg={6}>
                <Card>
                  <CardHeader>
                    <CardTitle className="d-flex align-items-center gap-2">
                      <Target className="text-warning" size={20} />
                      Profitability Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col md={6}>
                        <div className="text-center">
                          <h3 className="text-primary">{formatCurrency(dashboardData.profitability.grossRevenue)}</h3>
                          <p className="text-muted">Gross Revenue</p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-center">
                          <h3 className="text-danger">{formatCurrency(dashboardData.profitability.operationalCosts)}</h3>
                          <p className="text-muted">Operational Costs</p>
                        </div>
                      </Col>
                    </Row>
                    <div className="mt-3">
                      <div className="d-flex justify-content-between">
                        <span>Net Profit</span>
                        <span className="text-success fw-bold">{formatCurrency(dashboardData.profitability.netProfit)}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Profit Margin</span>
                        <span className="text-info fw-bold">{formatPercentage(dashboardData.profitability.profitMargin)}</span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </>
          ) : (
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <CardTitle className="d-flex align-items-center gap-2">
                    <Target className="text-warning" size={20} />
                    Profitability Analysis
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      <div className="text-center">
                        <h3 className="text-primary">{formatCurrency(dashboardData.profitability.grossRevenue)}</h3>
                        <p className="text-muted">Gross Revenue</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="text-center">
                        <h3 className="text-danger">{formatCurrency(dashboardData.profitability.operationalCosts)}</h3>
                        <p className="text-muted">Operational Costs</p>
                      </div>
                    </Col>
                  </Row>
                  <div className="mt-3">
                    <div className="d-flex justify-content-between">
                      <span>Net Profit</span>
                      <span className="text-success fw-bold">{formatCurrency(dashboardData.profitability.netProfit)}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Profit Margin</span>
                      <span className="text-info fw-bold">{formatPercentage(dashboardData.profitability.profitMargin)}</span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>

        <Row className="mb-4">
          <Col lg={12}>
            <Card>
              <CardHeader>
                <CardTitle className="d-flex align-items-center gap-2">
                  <CreditCard className="text-primary" size={20} />
                  Payroll Overview
                </CardTitle>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={3}>
                    <div className="text-center">
                      <h4 className="text-primary">{formatCurrency(dashboardData.payrollOverview.totalPayroll)}</h4>
                      <p className="text-muted">Total Payroll</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h4 className="text-warning">{dashboardData.payrollOverview.pendingAdvances}</h4>
                      <p className="text-muted">Pending Advances</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h4 className="text-success">{dashboardData.payrollOverview.approvedAdvances}</h4>
                      <p className="text-muted">Approved Advances</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h4 className="text-danger">{formatCurrency(dashboardData.payrollOverview.payrollCost)}</h4>
                      <p className="text-muted">Total Payroll Cost</p>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>


        {/* Business Intelligence */}
        <Row className="mb-4">
          <Col lg={12}>
            <h4 className="mb-3">
              <Activity className="me-2" size={24} />
              Business Intelligence
            </h4>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col lg={6}>
            <Card>
              <CardHeader>
                <CardTitle className="d-flex align-items-center gap-2">
                  <Users className="text-primary" size={20} />
                  Customer Analytics
                </CardTitle>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <div className="text-center">
                      <h3 className="text-primary">{dashboardData.customerAnalytics.totalCustomers}</h3>
                      <p className="text-muted">Total Customers</p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="text-center">
                      <h3 className="text-success">{dashboardData.customerAnalytics.newCustomers}</h3>
                      <p className="text-muted">New Customers</p>
                    </div>
                  </Col>
                </Row>
                <div className="mt-3">
                  <div className="d-flex justify-content-between">
                    <span>Returning Customers</span>
                    <span className="text-info">{dashboardData.customerAnalytics.returningCustomers}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Growth Rate</span>
                    <span className="text-success">{formatPercentage(dashboardData.customerAnalytics.customerGrowth)}</span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={6}>
            <Card>
              <CardHeader>
                <CardTitle className="d-flex align-items-center gap-2">
                  <Star className="text-warning" size={20} />
                  {branchConfig.allowServices ? "Popular Services" : "Popular Products"}
                </CardTitle>
              </CardHeader>
              <CardBody>
                {dashboardData.servicePerformance.popularServices.length > 0 ? (
                  <div className="table-responsive">
                    <Table className="table-sm">
                      <thead>
                        <tr>
                          <th>{branchConfig.allowServices ? "Service" : "Product"}</th>
                          <th>{branchConfig.allowServices ? "Bookings" : "Quantity Sold"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.servicePerformance.popularServices.map((service, index) => (
                          <tr key={index}>
                            <td>{service.serviceName}</td>
                            <td>{service.bookings}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert color="info" className="mb-0">
                    No data available for the selected period.
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>


        {/* Resource Utilization (Conditional on Services allowed) */}
        {branchConfig.allowServices && (
          <Row className="mb-4">
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <CardTitle className="d-flex align-items-center gap-2">
                    <Wrench className="text-primary" size={20} />
                    Resource Utilization
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={3}>
                      <div className="text-center">
                        <h4 className="text-primary">{dashboardData.resourceUtilization.totalRooms}</h4>
                        <p className="text-muted">Total Rooms</p>
                        <small className="text-info">
                          Occupancy: {formatPercentage(dashboardData.resourceUtilization.roomOccupancy)}
                        </small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <h4 className="text-info">{dashboardData.resourceUtilization.totalEquipment}</h4>
                        <p className="text-muted">Total Equipment</p>
                        <small className="text-success">
                          Usage: {formatPercentage(dashboardData.resourceUtilization.equipmentUsage)}
                        </small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <h4 className="text-success">{formatPercentage(dashboardData.operationalEfficiency.serviceCompletionRate)}</h4>
                        <p className="text-muted">Service Completion Rate</p>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <h4 className="text-warning">{dashboardData.operationalEfficiency.averageBookingTime.toFixed(1)}h</h4>
                        <p className="text-muted">Avg Booking Time</p>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}


        {/* Daily Operations Summary (Conditional on Services allowed) */}
        {branchConfig.allowServices && (
          <Row className="mb-4">
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <CardTitle className="d-flex align-items-center gap-2">
                    <Calendar className="text-primary" size={20} />
                    Today's Operations Summary
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={3}>
                      <div className="text-center">
                        <h4 className="text-primary">{dashboardData.dailyOperations.todaysBookings}</h4>
                        <p className="text-muted">Total Bookings</p>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <h4 className="text-success">{dashboardData.dailyOperations.completedBookings}</h4>
                        <p className="text-muted">Completed</p>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <h4 className="text-warning">{dashboardData.dailyOperations.cancelledBookings}</h4>
                        <p className="text-muted">Cancelled</p>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <h4 className="text-info">{dashboardData.dailyOperations.rescheduledBookings}</h4>
                        <p className="text-muted">Rescheduled</p>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

export default SpaManagerFinanceDashboard;
